// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  selectOneProductErrors,
  getCart,
  removeProducts,
  useDiscountDetails,
  type ProductError as CartProductError,
  type DecoratedProduct,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { mailboxes } from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';

import { Base } from '@gandi/ui';

import { fetchMailboxesPrices } from 'src/MailboxCreate/modules/actions';
import { useAppSelector } from 'src/hooks/useAppSelector';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItemBase from './components/ProductItemBase';
import { TitleRow } from './components/Rows';
import { DiscountRow } from './components/Rows/DiscountRow';
import { MailboxPlanRows } from './components/Rows/MailboxPlanRows';
import TotalRow from './components/Rows/TotalRow';

export type Props = {
  product: DecoratedProduct,
};

export function MailboxStandAloneRenew({ product }: Props): React.MixedElement {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const discountDetails = useDiscountDetails({ product });

  const isFetchingPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsFetchingOne(
      state,
      'mailboxv2_renew',
    ),
  );
  const isLoadedPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsLoadedOne(
      state,
      'mailboxv2_renew',
    ),
  );

  const productErrors: Array<CartProductError> = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  React.useEffect(() => {
    if (!isFetchingPrices && !isLoadedPrices) {
      dispatch(fetchMailboxesPrices('renew'));
    }
  }, [dispatch, isFetchingPrices, isLoadedPrices]);

  /**
   * Remove all mailboxes of a defined productType of the cart.
   */
  const removeFromCart = (e: any) => {
    e.preventDefault();

    if (product.quantity) {
      dispatch(removeProducts(product.ids.map(id => ({ id }))))
        .then(() => dispatch(getCart()))
        .catch(() => {
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          });
        });
    }
  };

  if (productErrors.length > 0) {
    return <ProductErrorItem label={undefined} errors={productErrors} />;
  }

  const priceCrossedOut = product.prices?.length
    ? product.computedTotalPrices?.beforeDiscount ||
      product.total?.before_discount
    : undefined;

  const price =
    product.computedTotalPrices?.beforeTaxes ?? product.total?.before_taxes;

  return (
    <ProductItemBase
      header={
        <>
          <TitleRow
            label={__('Mailbox')}
            productType={product.productType}
            handleRemoveAction={removeFromCart}
          />

          <Base flex gap="spacing-2x" flexCol>
            <MailboxPlanRows product={product} />
          </Base>
        </>
      }
      footer={
        <TotalRow
          hasTaxes={false}
          totalPrice={price}
          priceCrossedOut={priceCrossedOut}
          hasDiscount={!!discountDetails?.length}
        />
      }
    >
      {discountDetails?.map(discount => (
        <DiscountRow
          key={`discount-${product.id}-${discount.label}`}
          label={discount.label}
          price={discount.price}
        />
      ))}
    </ProductItemBase>
  );
}
